import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CMD_TRANSLATION_COMPLETED } from './commandbar/events';
import CacheRevalidator from './cacheRevalidator';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.CancelReading = [];
    this.cacheRevalidator = new CacheRevalidator((action, payload) =>
      this.app.$store.dispatch(action, payload)
    );
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'conversation.destroyed': this.onConversationDestroyed,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.reading': this.onReadingOn,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'phone_status.updated': this.onPhoneStatusUpdate,
      'phone_status.disconnect': this.onPhoneStatusDisconnect,
      'phone_call.transferred': this.onPhoneCallTransferred,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'conversation.translation_completed': this.onTranslationCompleted,
      'conversation.orders_linked': this.onOrdersLinked,
      'phone_queues.updated': this.onPhoneQueuesUpdate,
      'missed_calls_count.updated': this.onMissedCallsCountUpdate,
      'wallboard_stats.updated': this.onWallboardStatsUpdate,
      'data_assistant.message_created': this.onDataAssistantMessage,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (data.contacts) {
      this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    }
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onPhoneStatusUpdate = data => {
    this.app.$store.dispatch('agents/updatePhoneStatus', data);
    this.app.$store.dispatch('userPhoneCredentials/updateFromWebsocket', data);
  };

  onPhoneStatusDisconnect = () => {
    this.app.$store.dispatch('phone/requestDisconnect');
  };

  onPhoneCallTransferred = ({ conversation_id: conversationId }) => {
    this.app.$store.dispatch('phone/openTransferredConversation', {
      conversationId,
    });
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
    this.syncContactConversation(payload);
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.syncContactConversation(payload);
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.syncContactConversation(data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('contactConversations/addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.syncContactConversation(data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.syncContactConversation(data);
    this.fetchConversationStats();
  };

  onConversationDestroyed = data => {
    this.app.$store.dispatch('deleteConversation', data.id);
    this.app.$store.dispatch(
      'contactConversations/removeConversation',
      data.id
    );
    this.fetchConversationStats();
  };

  // Keeps the contact's conversation history (sidebar list, open conversations
  // banner) in step with the conversation list.
  syncContactConversation = data => {
    this.app.$store.dispatch('contactConversations/upsertConversation', data);
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  onReadingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearReadingTimer(conversationId, user);
    this.app.$store.dispatch('conversationReadingStatus/create', {
      conversationId,
      user,
    });
    this.initReadingTimer({ conversation, user });
  };

  clearReadingTimer = (conversationId, user) => {
    const key = `${conversationId}_${user?.id}`;
    const timerEvent = this.CancelReading[key];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelReading[key] = null;
    }
  };

  initReadingTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    const key = `${conversationId}_${user?.id}`;
    // Expire reading status after 35 seconds (5s grace over the 30s send interval)
    this.CancelReading[key] = setTimeout(() => {
      this.app.$store.dispatch('conversationReadingStatus/destroy', {
        conversationId,
        user,
      });
    }, 35000);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCacheInvalidate = data => {
    this.cacheRevalidator.push(data.cache_keys);
  };

  onOrdersLinked = data => {
    if (this.isAValidEvent(data)) {
      emitter.emit(BUS_EVENTS.CONVERSATION_ORDERS_LINKED, data);
    }
  };

  onTranslationCompleted = data => {
    if (this.isAValidEvent(data)) {
      emitter.emit(CMD_TRANSLATION_COMPLETED, data);
    }
  };

  onPhoneQueuesUpdate = data => {
    this.app.$store.dispatch('phoneQueues/updateFromWebsocket', data);
  };

  onMissedCallsCountUpdate = data => {
    this.app.$store.dispatch('phoneQueues/updateMissedCallsCount', data.count);
  };

  onWallboardStatsUpdate = data => {
    this.app.$store.dispatch(
      'phoneQueues/updateWallboardStatsFromWebsocket',
      data
    );
  };

  onDataAssistantMessage = data => {
    this.app.$store.dispatch('dataAssistant/receiveMessage', data);
  };
}

import { setPhoneLogSubscription } from 'dashboard/store/modules/phone';

export default {
  init(store, pubsubToken) {
    const connector = new ActionCableConnector({ $store: store }, pubsubToken);
    setPhoneLogSubscription(connector.subscription);
    return connector;
  },
};
